import type { TripConfig } from '../types';

export function localDateKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

/** Date-only arithmetic, independent of DST or device UTC offset. */
export function calendarDayNumber(date: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date ?? '')) return null;
  const parsed = new Date(`${date}T00:00:00Z`);
  if (isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) return null;
  return parsed.getTime() / 86400000;
}

/**
 * A calendar date as a Date you can safely format in local time.
 *
 * `new Date('2026-04-10')` is UTC midnight — the spec parses a bare date as
 * UTC — and formatting that in local time anywhere west of UTC gives the 9th.
 * A travel app is exactly where that bites: the device timezone moves with the
 * traveller, so a Melbourne user in New York saw every trip date, check-in and
 * "dates changed" notification a day early.
 *
 * Local noon for a bare date (it can't cross a day in any real offset), and a
 * normal parse for anything carrying a time, which is already local wall-clock.
 */
export function calendarDate(value: string): Date {
  const text = value || '';
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00` : text);
}

/**
 * N days on from a date, as a date string. Same UTC-only arithmetic as
 * calendarDayNumber above, for the same reason.
 *
 * Two copies of this existed, in ImportItineraryModal and DateRangePicker, and
 * both parsed `${date}T12:00` — LOCAL noon — then read the day back out of
 * toISOString(), which is UTC. Noon absorbs a DST hour, which is what it was
 * there for, but it does not absorb a whole offset: anywhere past UTC+12 —
 * Chatham, Kiritimati, and New Zealand for half the year — local noon is still
 * the previous day in UTC, so `addDays(date, 0)` returned YESTERDAY and every
 * date derived from it was a day early.
 *
 * Nothing about a calendar date needs a clock. Doing it in UTC has no edge.
 */
export function addDays(date: string, days: number): string {
  if (!date) return '';
  const parsed = new Date(`${date.slice(0, 10)}T00:00:00Z`);
  if (isNaN(parsed.getTime())) return '';
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

/** The part of [startDate, endDate] inside Open-Meteo's forecast (today .. +15:
 *  16 days counting today), as calendar-date strings; null when none of it is. */
export function forecastWindow(startDate: string, endDate: string, today: string): { start: string; end: string } | null {
  // 16 days COUNTING today, so the last one is today+15 — +16 was a 400.
  const windowEnd = addDays(today, 15);
  const start = startDate.slice(0, 10) < today ? today : startDate.slice(0, 10);
  const end = endDate.slice(0, 10) > windowEnd ? windowEnd : endDate.slice(0, 10);
  return start <= end ? { start, end } : null;
}

export function getTripTiming(config: TripConfig, today = localDateKey()) {
  const start = calendarDayNumber(config.startDate);
  const end = calendarDayNumber(config.endDate);
  const current = calendarDayNumber(today);
  const valid = start !== null && end !== null && current !== null && start <= end;
  const totalDays = valid ? end - start + 1 : 0;
  const elapsedDays = valid ? Math.max(0, Math.min(totalDays, current - start)) : 0;
  let status: 'setup' | 'countdown' | 'active' | 'post-trip' = 'setup';
  if (valid && config.title.trim() && config.destinations?.length) {
    status = current < start ? 'countdown' : current > end ? 'post-trip' : 'active';
  }
  return {
    status, today, totalDays, elapsedDays,
    daysUntil: valid ? Math.max(0, start - current) : 0,
    dayNumber: valid ? Math.max(1, Math.min(totalDays, current - start + 1)) : 0,
  };
}

export function msUntilNextLocalDay(now = new Date()): number {
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  return midnight.getTime() - now.getTime();
}
